import { LDClient, LDEvaluationDetail } from 'launchdarkly-react-client-sdk'
import type {
  FeatureFlag,
  BooleanFlag,
  ExperimentInput,
  ExperimentOutput
} from './feature-flag-types'
import { FeatureFlagClient } from './feature-flag-client'

/**
 * Widen boolean literal types (true/false) to boolean
 * This allows flags with defaultValue: false to be typed as boolean instead of literal false
 */
type WidenBoolean<T> = T extends boolean ? boolean : T

/**
 * Infer the type of a feature flag from its defaultValue
 * Boolean flags (defaultValue: true/false) are widened to boolean
 * Other types are preserved as-is
 */
type InferFlagType<T> = T extends { readonly defaultValue: infer D } ? WidenBoolean<D> : unknown

/**
 * Get feature flag value with LaunchDarkly client
 * Async/await pattern - use in components with useLDClient()
 *
 * @param flag - Feature flag definition
 * @param ldClient - LaunchDarkly client (from useLDClient hook)
 * @returns Flag value or default if not ready (type automatically inferred from flag's defaultValue)
 *
 * @example
 * const ldClient = useLDClient()
 * const isEnabled = getFlagValue(FeatureFlagName.I18N, ldClient)
 * // isEnabled is automatically typed as boolean
 *
 * @example
 * const ldClient = useLDClient()
 * const companies = getFlagValue(FeatureFlagName.COMPANIES_WITH_CUSTOM_HELP_CENTER, ldClient)
 * // companies type is inferred from the flag's defaultValue
 */
export function getFlagValue<T extends FeatureFlag>(
  flag: T,
  ldClient?: LDClient | undefined
): InferFlagType<T>
export function getFlagValue(flag: FeatureFlag, ldClient: LDClient | undefined): unknown {
  // Use internally identified client if available (prevents race conditions)
  const clientToUse = FeatureFlagClient.getClient() || ldClient

  if (!clientToUse) {
    return flag.defaultValue
  }

  // Ensure LaunchDarkly is fully initialized before getting flag values
  if (!FeatureFlagClient.isReady()) {
    console.warn(`[FeatureFlag] Not ready for '${flag.name}', using default:`, flag.defaultValue)
    return flag.defaultValue
  }

  return clientToUse.variation(flag.name, flag.defaultValue)
}

/**
 * Check if boolean feature flag is enabled
 * Async/await pattern - use in components with useLDClient()
 *
 * @param flag - Boolean feature flag
 * @param ldClient - LaunchDarkly client
 * @returns true if enabled, false otherwise
 *
 * @example
 * const ldClient = useLDClient()
 * const enabled = isFlagEnabled(FeatureFlagName.I18N, ldClient)
 */
export function isFlagEnabled(flag: BooleanFlag, ldClient: LDClient | undefined): boolean {
  return getFlagValue(flag, ldClient) as boolean
}

/**
 * Check if feature flag is enabled (synchronous, uses cached values)
 * Use in route loaders, guards, or anywhere LDClient isn't available
 *
 * @param flag - Feature flag definition
 * @returns Flag value from cache or default
 *
 * @example
 * // In route loader
 * const enabled = checkFlagEnabled(FeatureFlagName.I18N)
 */
export function checkFlagEnabled(flag: FeatureFlag): boolean {
  const flagsMap = FeatureFlagClient.getFlagsMap()
  return (flagsMap?.[flag.name] ?? flag.defaultValue) as boolean
}

/**
 * Get feature flag value (synchronous, uses cached values)
 *
 * @param flag - Feature flag definition
 * @returns Flag value from cache or default (type automatically inferred from flag's defaultValue)
 *
 * @example
 * const engageData = checkFlagValue(FeatureFlagName.ENGAGE_DATA)
 * // engageData type is inferred from the flag's defaultValue
 */
export function checkFlagValue<T extends FeatureFlag>(flag: T): InferFlagType<T>
export function checkFlagValue(flag: FeatureFlag): unknown {
  const flagsMap = FeatureFlagClient.getFlagsMap()
  return flagsMap?.[flag.name] ?? flag.defaultValue
}

/**
 * Check if a flag's value includes a specific item (for array flags)
 *
 * @param flag - Feature flag (should be array type)
 * @param value - Value to check for inclusion
 * @param ldClient - LaunchDarkly client
 * @returns true if value is in the array
 *
 * @example
 * const ldClient = useLDClient()
 * const hasCompany = flagIncludesValue(FeatureFlagName.COMPANIES_ON_ENROLLMENT_ROLLOUT, companyId, ldClient)
 */
export function flagIncludesValue(
  flag: FeatureFlag,
  value: string | number,
  ldClient: LDClient | undefined
): boolean {
  const flagValue = getFlagValue(flag, ldClient)

  if (!Array.isArray(flagValue)) {
    console.error(`Feature flag '${flag.name}' value is not an array:`, flagValue)
    return false
  }

  return flagValue.includes(value)
}

/**
 * Get variation detail for experiments
 *
 * @param flag - Feature flag definition
 * @returns Evaluation detail with reason and value
 */
export function getFlagVariationDetail(flag: FeatureFlag): LDEvaluationDetail {
  const client = FeatureFlagClient.getClient()

  if (!client) {
    return {
      value: flag.defaultValue
    }
  }

  return client.variationDetail(flag.name, flag.defaultValue)
}

/**
 * Check if LaunchDarkly is ready
 * Useful for preventing race conditions in loaders
 */
export function isFeatureFlagSystemReady(): boolean {
  return FeatureFlagClient.isReady()
}

/**
 * Map experiment value to variant name
 * @internal
 */
const mapExperimentValueToVariant = (value: boolean): string => {
  return value ? 'B' : 'A'
}

/**
 * Generate experiment data from evaluation detail
 *
 * @param input - LaunchDarkly evaluation detail
 * @returns Experiment output with variant info
 */
export function generateExperiments(input: ExperimentInput): ExperimentOutput {
  return {
    inExperiment: input.reason?.inExperiment ?? false,
    variantName: mapExperimentValueToVariant(input.value),
    kind: input.reason?.kind ?? 'UNKNOWN'
  }
}
